// src/Routes/admin/user-delete/page.tsx
import { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { gql } from "../../../__generated__";
import clsx from "clsx";
import Modal from "../../../components/common/modal";
import { notify, notifyError } from "../../../utils/toast";
import LoadingSpinnerInline from "../../../components/common/loading-spinner/loading-spinner-inline";
import { AdminSearchListDetailLayout } from "../components/admin-search-list-detail-layout";
import type {
  SearchUsersForDeleteQuery,
  SearchUsersForDeleteQueryVariables,
  AdminDeleteUserMutation,
  AdminDeleteUserMutationVariables,
} from "../../../__generated__/graphql";

const SEARCH_USERS = gql(`
  query SearchUsersForDelete($keyword: String) {
    searchUsers(keyword: $keyword) {
      id
      name
      email
      phone
      createdAt
      isDeleted
      deletedAt
    }
  }
`);

const ADMIN_DELETE_USER = gql(`
  mutation AdminDeleteUser($userId: Int!) {
    adminDeleteUser(userId: $userId) {
      ok
      error
    }
  }
`);

type UserRow = NonNullable<
  NonNullable<SearchUsersForDeleteQuery["searchUsers"]>[number]
>;

const UserDelete = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [searchUsers, { data: userData, loading: userLoading, refetch }] =
    useLazyQuery<SearchUsersForDeleteQuery, SearchUsersForDeleteQueryVariables>(
      SEARCH_USERS,
      { fetchPolicy: "network-only" }
    );

  const [adminDeleteUser, { loading: isDeleting }] = useMutation<
    AdminDeleteUserMutation,
    AdminDeleteUserMutationVariables
  >(ADMIN_DELETE_USER, {
    onCompleted: (data) => {
      if (data.adminDeleteUser.ok) {
        notify("유저가 삭제되었습니다.");
        setSelectedUser(null);
        setSearchKeyword("");
        setIsOpenModal(false);
        setDeleteConfirmText("");
        refetch();
      } else {
        notifyError(
          data.adminDeleteUser.error ?? "유저 삭제에 실패했습니다."
        );
      }
    },
    onError: (error) => {
      notifyError(`유저 삭제 중 오류가 발생했습니다: ${error.message}`);
    },
  });

  const handleSearch = () => {
    const term = searchKeyword.trim();
    if (!term) {
      notifyError("검색어를 입력해주세요.");
      return;
    }
    searchUsers({ variables: { keyword: term } });
  };

  const handleDeleteUser = () => {
    if (selectedUser && deleteConfirmText === "삭제") {
      adminDeleteUser({
        variables: { userId: selectedUser.id },
      });
      setDeleteConfirmText("");
    }
  };

  const handleOpenModal = () => {
    setIsOpenModal(true);
    setDeleteConfirmText("");
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setDeleteConfirmText("");
  };

  const users = (userData?.searchUsers ?? []).filter(
    (u): u is UserRow => u != null
  );

  const listContent =
    userLoading && users.length === 0 ? (
      <div className="flex justify-center py-8">
        <LoadingSpinnerInline />
      </div>
    ) : users.length === 0 ? (
      <div className="p-6 text-center text-gray-500 text-sm">
        검색어를 입력하고 검색 버튼을 누르세요.
      </div>
    ) : (
      <ul className="divide-y divide-gray-200">
        {users.map((user) => {
          const isSelected = selectedUser?.id === user.id;
          return (
            <li
              key={user.id}
              onClick={() => !user.isDeleted && setSelectedUser(user)}
              className={clsx(
                "px-4 py-3 cursor-pointer transition-colors",
                user.isDeleted && "cursor-not-allowed opacity-60",
                isSelected
                  ? "bg-blue-50 border-l-4 border-l-blue-600"
                  : "hover:bg-gray-50"
              )}
            >
              <div className="font-medium text-gray-900">
                {user.name ?? "이름 없음"} (ID: {user.id})
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user.email}
                {user.phone ? ` · ${user.phone}` : ""}
              </div>
              <div className="text-xs mt-1">
                {user.isDeleted ? (
                  <span className="text-red-600 font-semibold">삭제됨</span>
                ) : (
                  <span className="text-green-600 font-semibold">활성</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );

  const rightPanelContent =
    selectedUser && !selectedUser.isDeleted ? (
      <div className="space-y-4">
        <div className="text-sm text-gray-700">
          <span className="font-medium">선택 유저:</span>{" "}
          {selectedUser.name ?? "이름 없음"} (ID: {selectedUser.id})
        </div>

        <div className="bg-gray-50 p-3 rounded space-y-2 text-gray-700 text-sm">
          <div>
            <span className="font-medium text-gray-800">이메일:</span>{" "}
            {selectedUser.email ?? "—"}
          </div>
          <div>
            <span className="font-medium text-gray-800">연락처:</span>{" "}
            {selectedUser.phone ?? "—"}
          </div>
          <div>
            <span className="font-medium text-gray-800">가입일:</span>{" "}
            {new Date(selectedUser.createdAt).toLocaleDateString()}
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          이 작업은 되돌릴 수 없습니다.
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => setSelectedUser(null)}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-md hover:bg-gray-200"
          >
            선택 해제
          </button>
          <button
            type="button"
            onClick={handleOpenModal}
            className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            삭제하기
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      <AdminSearchListDetailLayout
        title="유저 삭제"
        description="삭제할 유저를 검색한 뒤 선택하여 삭제하세요."
        leftRatio={7}
        rightRatio={3}
        searchPlaceholder="이름·이메일·번호로 검색"
        searchValue={searchKeyword}
        onSearchChange={setSearchKeyword}
        onSearch={handleSearch}
        searchLoading={userLoading}
        step1Title="1. 유저 검색"
        step2Title="2. 검색 결과에서 선택"
        step3Title="3. 삭제처리"
        listContent={listContent}
        emptyListText="검색어를 입력하고 검색 버튼을 누르세요."
        rightPanelContent={rightPanelContent}
        rightPanelEmptyText="유저를 선택하세요."
      />

      <Modal
        isOpen={isOpenModal}
        title="정말 유저를 삭제하시겠습니까?"
        content={
          selectedUser ? (
            <div className="w-full px-4">
              <div className="text-center text-gray-600 text-sm mb-4 whitespace-pre-line">
                {`유저 ID: ${selectedUser.id}\n이름: ${selectedUser.name ?? "없음"}\n이메일: ${selectedUser.email ?? "없음"}`}
              </div>
              <div className="mb-4">
                <div className="text-sm text-gray-700 mb-2 font-semibold">
                  삭제를 확인하려면 아래에 "삭제"를 입력하세요
                </div>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="삭제"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          ) : null
        }
        onYes={handleDeleteUser}
        onClose={handleCloseModal}
        yesText={isDeleting ? "삭제 중..." : "삭제"}
        noText="취소"
        yesButtonDisabled={deleteConfirmText !== "삭제" || isDeleting}
      />
    </>
  );
};

export default UserDelete;
